import DocsLayout, { Callout } from '../../components/docs/DocsLayout';

export default function DocsWallets() {
  return (
    <DocsLayout 
      title="Wallets" 
      description="Multi-chain wallet management for AI agents"
    >
      <section className="space-y-6">
        <p className="text-white/70 leading-relaxed">
          Every AIBL agent comes with a smart account wallet. These ERC-4337 compliant wallets provide advanced features like batched transactions, gasless operations, and programmable access control.
        </p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Wallet Types</h2>

        <div className="space-y-4">
          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <h3 className="font-semibold text-white mb-2">Simple Account</h3>
            <p className="text-sm text-white/60">
              Standard ERC-4337 smart account. Single-signer wallet suitable for most agent use cases. Fast and cost-effective.
            </p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <h3 className="font-semibold text-white mb-2">Gnosis Safe</h3>
            <p className="text-sm text-white/60">
              Multi-signature wallet for enhanced security. Requires multiple approvals for transactions above configured thresholds.
            </p>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Supported Chains</h2>
        
        <p className="text-white/70 mb-4">
          AIBL supports wallets on 20+ EVM-compatible chains:
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            { name: 'Robinhood Network', status: 'primary' },
            { name: 'Ethereum', status: 'active' },
            { name: 'Arbitrum', status: 'active' },
            { name: 'Polygon', status: 'active' },
            { name: 'Optimism', status: 'active' },
            { name: 'Avalanche', status: 'active' },
            { name: 'BSC', status: 'active' },
            { name: 'Fantom', status: 'active' },
            { name: 'zkSync', status: 'coming' },
          ].map(chain => (
            <div 
              key={chain.name} 
              className={`p-3 rounded-lg border text-center text-sm ${
                chain.status === 'primary' 
                  ? 'bg-green-500/10 border-green-500/30 text-green-400' 
                  : chain.status === 'coming'
                    ? 'bg-white/5 border-white/10 text-white/40'
                    : 'bg-white/5 border-white/10 text-white/70'
              }`}
            >
              {chain.name}
              {chain.status === 'coming' && <span className="text-xs block mt-1">Coming Soon</span>}
            </div>
          ))}
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Funding Wallets</h2>
        
        <p className="text-white/70 mb-4">
          Agent wallets need to be funded with native tokens (ETH) to pay for gas fees. You can fund wallets by:
        </p>

        <ul className="space-y-2 text-white/70">
          <li>• Sending tokens directly to the wallet address from any external wallet</li>
          <li>• Using the built-in funding interface in the dashboard</li>
          <li>• Bridging assets from another chain</li>
        </ul>

        <Callout type="info" title="Gas Estimation">
          AIBL provides gas estimates before executing transactions. We recommend keeping at least 0.002 ETH in agent wallets to ensure operations don't fail due to insufficient gas.
        </Callout>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Turnkey EOA Signer</h2>
        
        <p className="text-white/70 mb-4">
          Behind every smart account is a Turnkey EOA (Externally Owned Account) signer. This EOA needs gas for certain operations:
        </p>

        <ul className="space-y-2 text-white/70">
          <li>• Initial smart account deployment</li>
          <li>• Gnosis Safe deployment and configuration</li>
          <li>• Multi-sig transaction execution</li>
        </ul>

        <Callout type="warning" title="Fund the EOA Signer">
          The EOA signer address is displayed in your dashboard. Make sure it has sufficient ETH for gas when deploying Safes or executing multi-sig transactions.
        </Callout>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Withdrawals</h2>
        
        <p className="text-white/70">
          To withdraw funds from an agent wallet, use the Transfer plugin or the dashboard withdrawal interface. Withdrawals are subject to your configured transfer limits and whitelisted addresses.
        </p>
      </section>
    </DocsLayout>
  );
}
